import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import AppointmentCard from "../components/agenda/AppointmentCard";
import AppointmentFormDialog from "../components/agenda/AppointmentFormDialog";
import {
  createAppointment,
  getAppointments,
  getPatients,
  updateAppointment,
  updateAppointmentStatus,
} from "../lib/api";
import {
  formatAgendaDate,
  getLocalDayRangeUtc,
  localFormToUtcIso,
  resolveBrowserTimezone,
  shiftLocalDateString,
  todayLocalDateString,
} from "../lib/agendaDateTime";
import {
  actionRequiresCancellationReason,
  actionToStatusPayload,
  summarizeDayCounts,
} from "../lib/appointmentStatus";
import { patientRecordLabel, patientProfilePath, resolvePatientRecordId } from "../lib/patientRecordId";
import { isUsClinicalHost } from "../lib/usClinicalHost";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

const SHOWCASE_APPOINTMENTS = [
  {
    id: "demo-1",
    patient_id: null,
    appointment_type: "office",
    status: "confirmed",
    start_at: null,
    end_at: null,
    reason_short: "Control general",
    buffer_before_minutes: 0,
    buffer_after_minutes: 10,
    timezone: "America/Guatemala",
    _demo_label: "M. R.",
  },
  {
    id: "demo-2",
    patient_id: null,
    appointment_type: "home",
    status: "scheduled",
    start_at: null,
    end_at: null,
    reason_short: "Visita domiciliaria",
    address: "Zona demo",
    buffer_before_minutes: 15,
    buffer_after_minutes: 15,
    timezone: "America/Guatemala",
    _demo_label: "J. L.",
  },
  {
    id: "demo-3",
    patient_id: null,
    appointment_type: "personal_block",
    status: "scheduled",
    start_at: null,
    end_at: null,
    reason_short: "Revisión de expedientes",
    buffer_before_minutes: 0,
    buffer_after_minutes: 0,
    timezone: "America/Guatemala",
  },
];

function humanApiError(error, english) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const detailText = typeof detail === "string" ? detail : "";

  if (status === 401) {
    return english ? "Your session expired. Please sign in again." : "La sesión venció. Inicie sesión de nuevo.";
  }
  if (status === 404 && /appointments/i.test(String(error?.config?.url || ""))) {
    return english
      ? "Agenda is temporarily unavailable."
      : "La Agenda está temporalmente no disponible.";
  }
  if (status === 404) {
    return english ? "Record not found." : "Registro no encontrado.";
  }
  if (status === 409) {
    if (/archivado/i.test(detailText) || /archived/i.test(detailText)) {
      return english
        ? "This patient is archived and cannot receive new appointments."
        : "El paciente está archivado y no admite citas nuevas.";
    }
    if (/cruza|conflicto|overlap|schedule/i.test(detailText) || /horario/i.test(detailText)) {
      return english
        ? "That time overlaps another appointment. Choose a different slot."
        : "Ese horario se cruza con otra cita. Elija otro horario.";
    }
    return (
      detailText ||
      (english ? "This change is not allowed right now." : "Este cambio no está permitido ahora.")
    );
  }
  if (status === 400 || status === 422) {
    if (Array.isArray(detail)) {
      return english ? "Please check the form fields." : "Revise los campos del formulario.";
    }
    return detailText || (english ? "Invalid data." : "Datos no válidos.");
  }
  if (!error?.response) {
    return english
      ? "Could not reach the server."
      : "No se pudo contactar al servidor.";
  }
  return english ? "Something went wrong. Try again." : "Ocurrió un problema. Intente de nuevo.";
}

function withShowcaseTimes(list, selectedDate, timeZone) {
  const slots = [
    ["08:30", "09:00"],
    ["10:15", "10:45"],
    ["15:00", "16:00"],
  ];
  return list.map((item, index) => {
    const [start, end] = slots[index % slots.length];
    return {
      ...item,
      start_at: localFormToUtcIso(selectedDate, start, timeZone),
      end_at: localFormToUtcIso(selectedDate, end, timeZone),
      timezone: timeZone,
    };
  });
}

export default function ClinicalAgendaV1({ showcaseMode = false }) {
  const english = isUsClinicalHost();
  const navigate = useNavigate();
  const timeZone = useMemo(() => resolveBrowserTimezone(), []);
  const locale = english ? "en-US" : "es-GT";

  const [selectedDate, setSelectedDate] = useState(() => todayLocalDateString(timeZone));
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [patientMap, setPatientMap] = useState({});
  const [loading, setLoading] = useState(!showcaseMode);
  const [error, setError] = useState("");
  const [endpointMissing, setEndpointMissing] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editing, setEditing] = useState(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelAction, setCancelAction] = useState("cancel_clinic");

  const counts = useMemo(() => summarizeDayCounts(appointments), [appointments]);

  const loadPatients = useCallback(async () => {
    if (showcaseMode) return;
    try {
      const res = await getPatients("all");
      const list = Array.isArray(res.data) ? res.data : [];
      setPatients(list);
      const map = {};
      list.forEach((p) => {
        const id = resolvePatientRecordId(p);
        if (!id) return;
        map[id] = {
          label: patientRecordLabel(p),
          archived: String(p.status || "").toLowerCase() === "archived",
          raw: p,
        };
      });
      setPatientMap(map);
    } catch {
      /* patients optional for viewing; form will show empty */
    }
  }, [showcaseMode]);

  const loadDay = useCallback(async () => {
    if (showcaseMode) {
      setAppointments(withShowcaseTimes(SHOWCASE_APPOINTMENTS, selectedDate, timeZone));
      setLoading(false);
      setError("");
      setEndpointMissing(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const range = getLocalDayRangeUtc(selectedDate, timeZone);
      const res = await getAppointments({
        start: range.start,
        end: range.end,
        limit: 200,
      });
      const list = Array.isArray(res.data) ? res.data : [];
      list.sort((a, b) => String(a.start_at).localeCompare(String(b.start_at)));
      setAppointments(list);
      setEndpointMissing(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 405 || status === 501) {
        setEndpointMissing(true);
        setError(
          english
            ? "Agenda is temporarily unavailable."
            : "La Agenda está temporalmente no disponible."
        );
      } else {
        setError(humanApiError(err, english));
      }
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [english, selectedDate, showcaseMode, timeZone]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  useEffect(() => {
    loadDay();
  }, [loadDay]);

  const openCreate = () => {
    setFormMode("create");
    setEditing(null);
    setFormError("");
    setFormOpen(true);
  };

  const handleFormSubmit = async (payload, meta) => {
    if (showcaseMode) return;
    if (saving) return;
    setSaving(true);
    setFormError("");
    try {
      if (meta.mode === "create") {
        await createAppointment(payload);
        toast.success(english ? "Appointment created" : "Cita creada");
      } else if (meta.mode === "reschedule") {
        await updateAppointmentStatus(meta.appointment.id, {
          status: "rescheduled",
          start_at: payload.start_at,
          end_at: payload.end_at,
          timezone: payload.timezone,
        });
        toast.success(english ? "Appointment rescheduled" : "Cita reprogramada");
      } else {
        const { patient_id, appointment_type, ...rest } = payload;
        await updateAppointment(meta.appointment.id, rest);
        toast.success(english ? "Appointment updated" : "Cita actualizada");
      }
      setFormOpen(false);
      await loadDay();
    } catch (err) {
      setFormError(humanApiError(err, english));
    } finally {
      setSaving(false);
    }
  };

  const runStatusAction = async (actionId, apt, extra = {}) => {
    if (showcaseMode) {
      toast.info(english ? "Design preview — no changes saved." : "Vista de diseño · sin cambios reales.");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const body = { ...actionToStatusPayload(actionId), ...extra };
      await updateAppointmentStatus(apt.id, body);
      toast.success(english ? "Status updated" : "Estado actualizado");
      await loadDay();
    } catch (err) {
      toast.error(humanApiError(err, english));
    } finally {
      setSaving(false);
    }
  };

  const handleAction = (actionId, apt) => {
    if (actionId === "edit") {
      setFormMode("edit");
      setEditing(apt);
      setFormError("");
      setFormOpen(true);
      return;
    }
    if (actionId === "reschedule") {
      setFormMode("reschedule");
      setEditing(apt);
      setFormError("");
      setFormOpen(true);
      return;
    }
    if (actionId === "open_record") {
      const path = patientProfilePath({ id: apt.patient_id });
      if (path) navigate(path);
      return;
    }
    if (actionId === "start_consultation") {
      if (!apt.patient_id) return;
      const info = patientMap[apt.patient_id];
      if (info?.archived) {
        toast.error(
          english
            ? "Cannot start a visit for an archived patient."
            : "No se puede iniciar consulta con un paciente archivado."
        );
        return;
      }
      navigate(`/nueva-consulta/${apt.patient_id}`);
      return;
    }
    if (actionRequiresCancellationReason(actionId)) {
      setCancelAction(actionId);
      setCancelTarget(apt);
      setCancelReason("");
      setCancelOpen(true);
      return;
    }
    runStatusAction(actionId, apt);
  };

  const confirmCancel = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    setCancelOpen(false);
    await runStatusAction(cancelAction, cancelTarget, {
      cancellation_reason: cancelReason.trim(),
    });
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 pb-10" data-testid="clinical-agenda-v1">
      {showcaseMode ? (
        <div
          className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-medium text-sky-800"
          data-testid="agenda-showcase-badge"
        >
          {english ? "Design preview · Sample data" : "Vista de diseño · Datos ficticios"}
        </div>
      ) : null}

      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{english ? "Agenda" : "Agenda"}</h1>
          <p className="mt-1 text-base text-slate-700" data-testid="agenda-selected-date">
            {formatAgendaDate(selectedDate, locale, timeZone)}
          </p>
          <p className="text-xs text-slate-500">{timeZone}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {showcaseMode ? (
            <Button asChild variant="outline">
              <Link to="/">{english ? "Back to home" : "Volver al inicio"}</Link>
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={() => setSelectedDate(todayLocalDateString(timeZone))}>
            {english ? "Today" : "Hoy"}
          </Button>
          <Button type="button" onClick={openCreate} data-testid="agenda-new-appointment">
            <Plus className="mr-1 h-4 w-4" />
            {english ? "New appointment" : "Nueva cita"}
          </Button>
        </div>
      </header>

      <nav className="flex flex-wrap items-center gap-2" aria-label={english ? "Day navigation" : "Navegación por día"}>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="min-h-10 min-w-10"
          aria-label={english ? "Previous day" : "Día anterior"}
          onClick={() => setSelectedDate((d) => shiftLocalDateString(d, -1))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Input
          type="date"
          className="w-auto min-h-10"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          aria-label={english ? "Select date" : "Seleccionar fecha"}
          data-testid="agenda-date-picker"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="min-h-10 min-w-10"
          aria-label={english ? "Next day" : "Día siguiente"}
          onClick={() => setSelectedDate((d) => shiftLocalDateString(d, 1))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </nav>

      <section
        className="grid grid-cols-2 gap-3 sm:grid-cols-4"
        aria-label={english ? "Day summary" : "Resumen del día"}
      >
        {[
          [english ? "Total" : "Total", counts.total],
          [english ? "Confirmed" : "Confirmadas", counts.confirmed],
          [english ? "Completed" : "Completadas", counts.completed],
          [english ? "Cancelled / no-show" : "Canceladas / no asistió", counts.cancelledOrNoShow],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-xl font-semibold text-slate-900">{value}</p>
          </div>
        ))}
      </section>

      {loading ? (
        <div className="space-y-3" aria-busy="true">
          <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
          <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
        </div>
      ) : null}

      {!loading && error ? (
        <div
          className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
          role="alert"
          data-testid="agenda-error"
        >
          {error}
          {endpointMissing ? null : (
            <Button type="button" variant="link" className="px-1" onClick={loadDay}>
              {english ? "Retry" : "Reintentar"}
            </Button>
          )}
        </div>
      ) : null}

      {!loading && !error && appointments.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center"
          data-testid="agenda-empty"
        >
          <p className="text-sm text-slate-700">
            {english ? "No appointments for this day." : "No hay citas para este día."}
          </p>
          <Button type="button" className="mt-4" onClick={openCreate}>
            {english ? "Create appointment" : "Crear cita"}
          </Button>
        </div>
      ) : null}

      {!loading && !error && appointments.length > 0 ? (
        <div className="space-y-3" data-testid="agenda-list">
          {appointments.map((apt) => {
            const info = apt.patient_id ? patientMap[apt.patient_id] : null;
            return (
              <AppointmentCard
                key={apt.id}
                appointment={apt}
                patientLabel={showcaseMode ? apt._demo_label || "M. R." : info?.label}
                patientArchived={Boolean(info?.archived)}
                english={english}
                showcaseMode={showcaseMode}
                onAction={handleAction}
                locale={locale}
                timeZone={timeZone}
              />
            );
          })}
        </div>
      ) : null}

      <AppointmentFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        mode={formMode}
        appointment={editing}
        patients={patients}
        english={english}
        timeZone={timeZone}
        saving={saving}
        showcaseMode={showcaseMode}
        onSubmit={handleFormSubmit}
        errorMessage={formError}
      />

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{english ? "Cancel appointment" : "Cancelar cita"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancellation_reason">{english ? "Reason" : "Motivo administrativo"}</Label>
            <Input
              id="cancellation_reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              data-testid="cancellation-reason"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCancelOpen(false)}>
              {english ? "Back" : "Volver"}
            </Button>
            <Button type="button" disabled={!cancelReason.trim() || saving} onClick={confirmCancel}>
              {english ? "Confirm cancel" : "Confirmar cancelación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
